import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, EmptyState, IconButton, Page, PageHeader, Pagination, TableSkeleton } from "../../../components/ui";
import {
  getMaintenanceRuns,
  getMaintenanceTasks,
  previewMaintenanceTask,
  runMaintenanceTask,
} from "../../../services/maintenance";
import SiteMaintenanceCard from "./SiteMaintenanceCard";
import TrafficModeCard from "./TrafficModeCard";
import "./maintenance.css";

const POLL_MS = 1500;

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

const GROUPS = [
  { key: "content", label: "Content" },
  { key: "payments", label: "Payments" },
  { key: "users", label: "Users" },
];

const STATUS = {
  running: { color: "blue", label: "In progress" },
  done: { color: "green", label: "Done" },
  failed: { color: "red", label: "Failed" },
  interrupted: { color: "amber", label: "Interrupted" },
};

const errorMessage = (error) => error?.response?.data?.message || error?.message || "";

const formatValue = (value) =>
  typeof value === "number" ? value.toLocaleString() : value === null || value === undefined ? "—" : String(value);

/** Figures of a run ({ "Videos checked": 120, … }). */
export const Summary = ({ summary }) => {
  const entries = Object.entries(summary || {});
  if (!entries.length) return null;
  return (
    <dl className="maint-summary">
      {entries.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{formatValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
};

const Progress = ({ run }) => {
  const { t } = useTranslation();
  const { done = 0, total } = run.progress || {};
  const percent = total ? Math.min(100, Math.round((done / total) * 100)) : null;
  return (
    <div className="maint-progress">
      <div className="maint-progress-bar" data-indeterminate={percent === null || undefined}>
        <span style={percent === null ? undefined : { width: `${percent}%` }} />
      </div>
      <span className="ui-hint">
        {run.mode === "apply" ? t("Running…") : t("Preview in progress…")}
        {total ? ` ${done.toLocaleString()} / ${total.toLocaleString()}` : ""}
      </span>
    </div>
  );
};

const RunResult = ({ run, title }) => {
  const { t } = useTranslation();
  const status = STATUS[run.status] || STATUS.done;
  return (
    <div className="maint-result" data-mode={run.mode}>
      <div className="maint-result-head">
        <span className="maint-result-title">{title}</span>
        <Badge color={status.color} dot>
          {t(status.label)}
        </Badge>
        <span className="ui-hint">
          {formatDate(run.startedAt)}
          {run.admin ? ` · ${run.admin}` : ""}
        </span>
      </div>
      {run.error && <p className="maint-error">{run.error}</p>}
      <Summary summary={run.summary} />
      {run.samples?.length > 0 && (
        <details className="maint-samples">
          <summary>{t("Examples")}</summary>
          <ul>
            {run.samples.map((line) => (
              <li key={line}>
                <code>{line}</code>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const TaskCard = ({ task, onPreview, onRun }) => {
  const { t } = useTranslation();
  const busy = Boolean(task.running);
  // The preview is shown while it is the latest information on the task.
  const freshPreview =
    task.lastPreview &&
    (!task.lastApply || new Date(task.lastPreview.startedAt) > new Date(task.lastApply.startedAt));
  return (
    <article className="ui-card maint-task" aria-label={task.label}>
      <header className="maint-task-head">
        <h3>{task.label}</h3>
        {task.risk === "caution" ? (
          <Badge color="amber">
            <i className="ri-error-warning-line" aria-hidden="true"></i>
            {t("Use with care")}
          </Badge>
        ) : (
          <Badge color="green">
            <i className="ri-shield-line" aria-hidden="true"></i>
            {t("Safe")}
          </Badge>
        )}
      </header>
      <p className="maint-task-text">{task.description}</p>

      {busy && <Progress run={task.running} />}
      {!busy && freshPreview && <RunResult run={task.lastPreview} title={t("Preview")} />}
      {!busy && task.lastApply && <RunResult run={task.lastApply} title={t("Last run")} />}
      {!busy && !task.lastApply && !freshPreview && (
        <p className="ui-hint">{t("Never run.")}</p>
      )}

      <footer className="maint-task-actions">
        <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => onPreview(task)}>
          <i className="ri-eye-line" aria-hidden="true"></i>
          {t("Preview")}
        </button>
        <button
          type="button"
          className={task.risk === "caution" ? "ui-btn ui-btn-danger" : "ui-btn"}
          disabled={busy}
          onClick={() => onRun(task)}
        >
          <i className="ri-play-line" aria-hidden="true"></i>
          {t("Run")}
        </button>
      </footer>
    </article>
  );
};

const History = ({ tasks, version }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const labelOf = (key) => tasks.find((task) => task.key === key)?.label || key;

  useEffect(() => {
    getMaintenanceRuns(page)
      .then(setData)
      .catch(() => setData({ items: [], total: 0, pageSize: 20 }));
  }, [page, version]);

  return (
    <section className="ui-card">
      <h2 className="maint-section-title maint-history-title">{t("History")}</h2>
      {!data ? (
        <TableSkeleton rows={4} />
      ) : !data.items.length ? (
        <EmptyState icon="ri-time-line">{t("No run yet")}</EmptyState>
      ) : (
        <>
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>{t("Task")}</th>
                  <th>{t("Type")}</th>
                  <th>{t("status")}</th>
                  <th>{t("By")}</th>
                  <th>{t("Date")}</th>
                  <th>{t("Result")}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((run) => {
                  const status = STATUS[run.status] || STATUS.done;
                  return (
                    <tr key={run.id}>
                      <td>{labelOf(run.task)}</td>
                      <td className="ui-cell-muted">{run.mode === "apply" ? t("Run") : t("Preview")}</td>
                      <td>
                        <Badge color={status.color} dot>
                          {t(status.label)}
                        </Badge>
                      </td>
                      <td className="ui-cell-muted">{run.admin || "—"}</td>
                      <td className="ui-cell-muted">{formatDate(run.startedAt)}</td>
                      <td className="maint-history-result">
                        {run.error ||
                          Object.entries(run.summary || {})
                            .map(([label, value]) => `${label} : ${formatValue(value)}`)
                            .join(" · ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))}
            total={data.total}
            pageSize={data.pageSize}
            onChange={setPage}
          />
        </>
      )}
    </section>
  );
};

const Maintenance = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState(null);
  const [confirming, setConfirming] = useState(null);
  // Bumped after each run, to reload the history.
  const [version, setVersion] = useState(0);

  // Stable: the polling below must not restart on every render.
  const tRef = useRef(t);
  tRef.current = t;
  const load = useCallback(
    () =>
      getMaintenanceTasks()
        .then(setTasks)
        .catch((error) => {
          setTasks((current) => current || []);
          toast.error(errorMessage(error) || tRef.current("Error fetching data"));
        }),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  // Follow the runs in progress.
  const running = (tasks || []).some((task) => task.running);
  useEffect(() => {
    if (!running) {
      setVersion((v) => v + 1);
      return undefined;
    }
    const timer = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer);
  }, [running, tasks, load]);

  const start = (request, task) =>
    request(task.key)
      .then(load)
      .catch((error) => toast.error(errorMessage(error) || t("Error updating data")));

  const confirmedPreview =
    confirming?.lastPreview?.status === "done" &&
    (!confirming.lastApply || new Date(confirming.lastPreview.startedAt) > new Date(confirming.lastApply.startedAt))
      ? confirming.lastPreview
      : null;

  return (
    <Page>
      <PageHeader
        title={t("Maintenance")}
        subtitle={t("Operations on the whole database. System admins only. Start with a preview: it changes nothing.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />}
      />

      <TrafficModeCard />
      <SiteMaintenanceCard />

      {!tasks ? (
        <section className="ui-card">
          <TableSkeleton rows={4} />
        </section>
      ) : (
        GROUPS.map((group) => {
          const groupTasks = tasks.filter((task) => task.group === group.key);
          if (!groupTasks.length) return null;
          return (
            <section key={group.key} className="maint-group">
              <h2 className="maint-section-title">{t(group.label)}</h2>
              <div className="maint-grid">
                {groupTasks.map((task) => (
                  <TaskCard
                    key={task.key}
                    task={task}
                    onPreview={(item) => start(previewMaintenanceTask, item)}
                    onRun={setConfirming}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      {tasks && <History tasks={tasks} version={version} />}

      <ConfirmDialog
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        onConfirm={() => start(runMaintenanceTask, confirming)}
        confirmLabel={t("Run")}
        danger={confirming?.risk === "caution"}
      >
        {confirming && (
          <>
            <p>
              {t("Run this task on the whole database?")} <strong>{confirming.label}</strong>
            </p>
            {confirmedPreview ? (
              <>
                <p className="ui-hint">{t("Latest preview:")}</p>
                <Summary summary={confirmedPreview.summary} />
              </>
            ) : (
              <p className="ui-hint">{t("No recent preview: run one first to see what will change.")}</p>
            )}
          </>
        )}
      </ConfirmDialog>
    </Page>
  );
};

export default Maintenance;
